<div class="bg-white max-w-full max-h-[80vh] overflow-y-auto relative modal-enter dark:bg-slate-900">
  <div class="p-10" *ngIf="currentStep === 1 && !onlyColor">
    <div class="text-center mb-10">
      <div
        class="text-gray-900 text-3xl font-bold mb-4 tracking-tight dark:text-slate-100 flex items-center justify-center">
        <span>Welcome to</span>
        <img src="assets/logos/rubi.png" alt="RUBi Logo" class="object-contain inline-block" style="width:120px; -mt-2">
      </div>
      <div class="space-y-3">
        <p class="text-gray-700 text-xl font-medium dark:text-slate-300">
          Hello <strong class="capitalize text-header">{{ getUserDisplayName() }}</strong>! 👋
        </p>
        <p class="text-gray-500 text-base leading-relaxed dark:text-slate-400">
          We're excited to have you here. Let's customize your experience to make you feel at home.
        </p>
      </div>
    </div>

    <div class="flex justify-between gap-4">
      <button
        class="flex-1 px-6 py-4 rounded-xl text-sm font-semibold cursor-pointer transition-all duration-200 bg-slate-50 text-slate-600 border-2 border-slate-200 shadow-sm hover:bg-slate-100 hover:border-slate-300 hover:-translate-y-0.5 hover:shadow-md dark:bg-slate-800 dark:text-slate-300 dark:border-slate-600 dark:hover:bg-slate-700 dark:hover:border-slate-500"
        (click)="closeOnboarding()">
        Skip for now
      </button>
      <button
        class="flex-1 px-6 py-4 rounded-xl text-sm font-semibold cursor-pointer transition-all duration-200 bg-header text-white shadow-md hover:shadow-lg hover:-translate-y-0.5 hover:bg-opacity-90 active:translate-y-0"
        (click)="nextStep()">
        Continue
      </button>
    </div>
  </div>

  <div class="p-10" *ngIf="currentStep === 2">
    <div class="text-center mb-8">
      <h2 class="text-gray-900 text-2xl font-bold mb-3 tracking-tight dark:text-slate-100">Choose your color</h2>
      <p class="text-gray-500 text-base dark:text-slate-400">Select the color that best represents you</p>
    </div>

    <div>
      <div class="text-center mb-8">
        <div
          class="w-20 h-20 rounded-full inline-flex items-center justify-center text-white text-3xl font-bold mb-3 transition-all duration-300 shadow-xl border-2 border-white border-opacity-20"
          [style.background-color]="selectedColor">
          {{ userInitials }}
        </div>
        <p class="text-gray-500 text-sm font-medium uppercase tracking-wide dark:text-slate-400">Preview</p>
      </div>

      <div class="mb-8">
        <h3 class="text-gray-900 text-lg font-semibold mb-4 text-center dark:text-slate-100">Popular colors</h3>
        <div class="grid grid-cols-[repeat(auto-fit,minmax(48px,1fr))] gap-3 max-w-md mx-auto">
          <div *ngFor="let color of predefinedColors"
            class="w-12 h-12 rounded-xl cursor-pointer transition-all duration-300 relative flex items-center justify-center border-2 border-transparent shadow-md hover:-translate-y-0.5 hover:scale-105 hover:shadow-xl"
            [class.selected]="selectedColor === color" [class.-translate-y-0.5]="selectedColor === color"
            [class.scale-110]="selectedColor === color" [class.shadow-2xl]="selectedColor === color"
            [class.border-white]="selectedColor === color" [class.border-opacity-90]="selectedColor === color"
            [style.background-color]="color" (click)="selectPredefinedColor(color)" [attr.title]="color">
            <lucide-icon name="check" size="16" color="white" *ngIf="selectedColor === color"
              class="drop-shadow-md"></lucide-icon>
          </div>

          <div *ngIf="isCustomColor"
            class="w-12 h-12 rounded-xl cursor-pointer transition-all duration-300 relative flex items-center justify-center border-2 border-transparent shadow-md hover:-translate-y-0.5 hover:scale-105 hover:shadow-xl selected scale-110 shadow-2xl border-white border-opacity-90"
            [style.background-color]="selectedColor" (click)="selectPredefinedColor(selectedColor)"
            [attr.title]="selectedColor">
            <lucide-icon name="check" size="16" color="white" class="drop-shadow-md"></lucide-icon>
          </div>
        </div>
      </div>

      <div class="text-center">
        <button
          class="inline-flex items-center gap-2 px-5 py-3 border-2 border-gray-200 bg-white rounded-xl text-gray-500 text-sm font-medium cursor-pointer transition-all duration-200 shadow-sm hover:border-header hover:text-header hover:shadow-md hover:-translate-y-0.5 dark:bg-slate-800 dark:border-slate-600 dark:text-slate-300 dark:hover:border-header dark:hover:text-header"
          (click)="toggleAdvancedPicker()" type="button">
          <lucide-icon name="palette" size="16"></lucide-icon>
          {{ showAdvancedPicker ? 'Hide' : 'More colors' }}
        </button>

        <div class="mt-5 p-5 border-2 border-gray-100 rounded-2xl bg-slate-50 dark:bg-slate-800 dark:border-slate-600"
          *ngIf="showAdvancedPicker">
          <div class="flex flex-col items-center gap-3">
            <label for="customColor" class="text-sm text-gray-500 font-medium dark:text-slate-300">Choose a custom
              color:</label>
            <input type="color" id="customColor" [value]="selectedColor" (change)="onColorChange($event)"
              class="w-16 h-12 border-none rounded-xl cursor-pointer shadow-md transition-all duration-300 border-2 border-white hover:scale-105 hover:shadow-lg">
            <span
              class="text-xs text-gray-400 font-mono font-semibold uppercase tracking-wide bg-gray-100 px-2 py-1 rounded dark:bg-slate-700 dark:text-slate-400">{{
              selectedColor }}</span>
          </div>
        </div>
      </div>
    </div>

    <div class="flex justify-between gap-4 mt-10" *ngIf="!onlyColor">
      <button
        class="flex-1 px-6 py-4 rounded-xl text-sm font-semibold cursor-pointer transition-all duration-200 bg-slate-50 text-slate-600 border-2 border-slate-200 shadow-sm hover:bg-slate-100 hover:border-slate-300 hover:-translate-y-0.5 hover:shadow-md dark:bg-slate-800 dark:text-slate-300 dark:border-slate-600 dark:hover:bg-slate-700 dark:hover:border-slate-500"
        (click)="previousStep()">
        Back
      </button>
      <button
        class="flex-1 px-6 py-4 rounded-xl text-sm font-semibold cursor-pointer transition-all duration-200 bg-header text-white shadow-md hover:shadow-lg hover:-translate-y-0.5 hover:bg-opacity-90 active:translate-y-0"
        (click)="completeOnboarding()">
        Continue
      </button>
    </div>
    <div class="flex justify-center mt-10" *ngIf="onlyColor">
      <button
        class="px-8 py-4 rounded-xl text-base font-semibold cursor-pointer transition-all duration-200 bg-header text-white shadow-md hover:shadow-lg hover:-translate-y-0.5 hover:bg-opacity-90 active:translate-y-0"
        (click)="completeOnboarding()">
        Save Color
      </button>
    </div>
  </div>

  <button
    class="absolute top-5 right-5 bg-white bg-opacity-90 border-none cursor-pointer p-2.5 rounded-full transition-all duration-200 text-gray-500 z-10 backdrop-blur-sm shadow-md hover:bg-slate-50 hover:text-gray-700 hover:scale-105 hover:shadow-lg dark:bg-slate-800 dark:bg-opacity-90 dark:text-slate-400 dark:hover:bg-slate-700 dark:hover:text-slate-200"
    (click)="closeOnboarding()" type="button">
    <lucide-icon name="x" size="20"></lucide-icon>
  </button>
</div>
